// Single source of truth for which exam each content module belongs to and how
// the Home grid + Library group them. Home renders a target-aware grid from
// orderedSections(); PackBrowser groups the Library from the same data. Keep the
// module keys in sync with the api pack allowlist + gen-corpus-counts MODULES.
import type { ExamTarget } from "./api.js";

export type ExamTag = ExamTarget; // "SAT" | "PSAT" | "ACT" | "AP" | "APPS"

/** Which exam(s) each module is relevant to (used by the Library exam filter). */
export const MODULE_EXAMS: Record<string, ExamTag[]> = {
  trapline: ["SAT", "PSAT", "ACT"],
  grammar: ["SAT", "PSAT", "ACT"],
  slice: ["SAT", "PSAT", "ACT"],
  science: ["SAT"],
  prompt: ["APPS"],
  rubric: ["AP"],
  calc: ["AP"],
  apprecalc: ["AP"],
  act_math: ["ACT"],
  act_english: ["ACT"],
  act_reading: ["ACT"],
  act_science: ["ACT"],
};

export const SECTION_LABEL: Record<ExamTag, string> = {
  SAT: "SAT",
  PSAT: "PSAT",
  ACT: "ACT",
  AP: "AP",
  APPS: "Application essays",
};

// The exam-specific modules per exam for the Home grid. The ACT list is only
// the four ACT-format sections; the SAT-format pattern drills that also help an
// ACT student live in ACT_EXTRA_PRACTICE below, shown as a separate labeled
// group so the two never read as duplicate lanes ("ACT Reading" vs "Reading").
const EXAM_MODULES: Record<ExamTag, string[]> = {
  SAT: ["trapline", "grammar", "slice", "science"],
  // THE SAME MODULES AS SAT, and that is the point rather than an oversight.
  // PSAT/NMSQT tests the same skills from the same domains; what differs is the
  // blueprint weighting and a handful of SAT-only topics, both of which live in
  // the form builder. A PSAT student practices SAT Reading, Grammar and Math
  // because that IS their content. The dedup below keeps these from appearing
  // twice for somebody targeting both.
  PSAT: ["trapline", "grammar", "slice"],
  ACT: ["act_math", "act_english", "act_reading", "act_science"],
  AP: ["rubric", "calc", "apprecalc"],
  APPS: ["prompt"],
};

// SAT-format pattern drills whose skills transfer to the ACT. Shown to an
// ACT-only student as a secondary "Extra pattern practice" group beneath the
// four ACT sections. When SAT is ALSO targeted these already appear under the
// SAT section, so the dedup below keeps them from repeating.
const ACT_EXTRA_PRACTICE = ["trapline", "grammar", "slice"];
const ACT_EXTRA_LABEL = "Extra pattern practice";

const EXAM_ORDER: ExamTag[] = ["SAT", "PSAT", "ACT", "AP", "APPS"];

export type ModuleSection = { key: string; exam: ExamTag; label: string; modules: string[] };

/**
 * The module sections to show on Home for a user's chosen exam target(s), in
 * canonical exam order, each module appearing once. Shared SAT/ACT skill drills
 * land under SAT when SAT is targeted; an ACT-only student sees the four ACT
 * sections plus a labeled "Extra pattern practice" group. Only the user's
 * targets are returned; the full catalog lives in the Library. Empty targets
 * fall back to SAT. `key` is unique per section (safe as a React list key even
 * when two sections share an exam).
 */
export function targetModuleSections(targets: ExamTag[]): ModuleSection[] {
  const t = targets && targets.length ? targets : (["SAT"] as ExamTag[]);
  const seen = new Set<string>();
  const out: ModuleSection[] = [];
  const actOnly = t.includes("ACT") && !t.includes("SAT");
  for (const exam of EXAM_ORDER) {
    if (!t.includes(exam)) continue;
    const mods = EXAM_MODULES[exam].filter((m) => !seen.has(m));
    mods.forEach((m) => seen.add(m));
    if (mods.length) {
      // Only the ACT core group gets the "ACT sections" header, and only when
      // the extra-practice group will follow it (so the two headers contrast).
      const label = exam === "ACT" && actOnly ? "ACT sections" : SECTION_LABEL[exam];
      out.push({ key: exam, exam, label, modules: mods });
    }
    if (exam === "ACT" && actOnly) {
      const extra = ACT_EXTRA_PRACTICE.filter((m) => !seen.has(m));
      extra.forEach((m) => seen.add(m));
      if (extra.length) {
        out.push({ key: "act_extra", exam: "ACT", label: ACT_EXTRA_LABEL, modules: extra });
      }
    }
  }
  return out;
}

/** First module of the user's primary target, the sensible default drill CTA. */
export function defaultModuleForTargets(targets: ExamTag[]): string {
  return targetModuleSections(targets)[0]?.modules[0] ?? "trapline";
}
